import React, {useId, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Flex,
    Grid,
    Image,
    Skeleton,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
    useStyleConfig,
} from '@chakra-ui/react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import SanitizedHtml from '../../html-sanitizer'
import PricingCard from './pricing-card'

export const PricingSection = ({heading, bodyCopy, tabs = []}) => {
    const styles = useStyleConfig('PricingSection')
    const instanceId = `pricing-section-${useId().replace(/:/g, '')}`
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const durationTabs = tabs.slice(0, 2)
    const selectedTabIndex = durationTabs[activeTabIndex] ? activeTabIndex : 0
    const groups = (durationTabs[selectedTabIndex]?.pricing_group || []).slice(0, 3)
    const productIds = [
        ...new Set(
            durationTabs
                .flatMap((tab) =>
                    (tab.pricing_group || [])
                        .slice(0, 3)
                        .flatMap((group) =>
                            (group.products?.data || []).slice(0, 1).map((product) => product.id),
                        ),
                )
                .filter(Boolean),
        ),
    ]
    const {
        data: productsData,
        isLoading,
        isError,
    } = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'images', 'prices', 'variations'],
            },
        },
        {enabled: productIds.length > 0},
    )
    const cards = groups.flatMap((group, groupIndex) =>
        (group.products?.data || []).slice(0, 1).flatMap((reference) => {
            const product = productsData?.data?.find((item) => item.id === reference.id)
            return product
                ? [
                      {
                          key: `${group._metadata?.uid || groupIndex}-${reference.id}`,
                          product,
                          timing: group.timing,
                          isBestSeller: group.is_best_seller,
                      },
                  ]
                : []
        }),
    )
    const cardCount = Math.max(1, isLoading ? groups.length : cards.length)

    if (!tabs.length || !productIds.length || isError) return null

    return (
        <Box {...styles.container} data-testid="pricing-section">
            {(heading || bodyCopy) && (
                <Flex {...styles.header}>
                    {heading && (
                        <Text as="h2" {...styles.heading}>
                            {heading}
                        </Text>
                    )}
                    {bodyCopy && (
                        <SanitizedHtml as={Box} html={bodyCopy} sanitize {...styles.bodyCopy} />
                    )}
                </Flex>
            )}
            <Tabs
                id={instanceId}
                index={selectedTabIndex}
                onChange={setActiveTabIndex}
                variant="unstyled"
                display="flex"
                flexDirection="column"
                gap="small-1.base"
                isLazy
                lazyBehavior="unmount"
            >
                {durationTabs.length > 1 && (
                    <TabList {...styles.tabList}>
                        {durationTabs.map((tab, index) => (
                            <Tab
                                key={tab._metadata?.uid || index}
                                {...styles.tab}
                                _selected={styles.tabActive}
                            >
                                {tab.icon?.url && (
                                    <Image
                                        src={tab.icon.url}
                                        alt=""
                                        aria-hidden="true"
                                        {...styles.tabIcon}
                                    />
                                )}
                                {tab.name}
                            </Tab>
                        ))}
                    </TabList>
                )}
                <TabPanels>
                    {durationTabs.map((tab, tabIndex) => (
                        <TabPanel key={tab._metadata?.uid || tabIndex} padding={0}>
                            <Grid
                                {...styles.grid}
                                data-testid="pricing-grid"
                                templateColumns={{
                                    base: 'minmax(0, 1fr)',
                                    md: `repeat(${cardCount}, minmax(0, 1fr))`,
                                }}
                                width={{base: '100%', md: `${(cardCount / 3) * 100}%`}}
                                marginInline={0}
                                aria-busy={isLoading}
                            >
                                {isLoading
                                    ? groups.map((group, index) => (
                                          <Skeleton
                                              key={group._metadata?.uid || index}
                                              height="400px"
                                          />
                                      ))
                                    : cards.map((card) => (
                                          <PricingCard
                                              key={card.key}
                                              product={card.product}
                                              timing={card.timing}
                                              isBestSeller={card.isBestSeller}
                                          />
                                      ))}
                            </Grid>
                        </TabPanel>
                    ))}
                </TabPanels>
            </Tabs>
        </Box>
    )
}

PricingSection.propTypes = {
    heading: PropTypes.string,
    bodyCopy: PropTypes.string,
    tabs: PropTypes.array,
}
